import { type useEntityAttachments } from '@Hooks/Attachments/useEntityAttachments';
import { useRemoteCacheInvalidation } from '@Hooks/useRemoteCacheInvalidation';
import { Button, defaultIconSize, useModalState } from '@synqo/frontend-component-library';
import { IconPaperclip } from '@tabler/icons-react';
import { type FC } from 'react';
import { AttachmentsModal } from './AttachmentsModal';

export interface EntityAttachmentsButtonProps {
    /** Result of a per-entity attachments hook (e.g. <c>useOrderAttachments</c>); the parent owns the query lifecycle. */
    attachmentsInfo: ReturnType<typeof useEntityAttachments>;
    /** Whether new files can be uploaded — e.g. <c>false</c> for orders in Approved/Completed state. */
    canUpload: boolean;
    /** Whether existing attachments can be downloaded. Note: download is also gated on the per-attachment scan status. */
    canDownload: boolean;
    /** Whether existing attachments can be deleted. */
    canDelete: boolean;
}

/**
 * Generic header action button for per-entity attachments. Opens an <c>AttachmentsModal</c>
 * scoped to the caller's <c>attachmentsInfo</c>. Locked until the options endpoint has
 * returned an allow-list (without MIME types / max size the modal can't render meaningfully).
 */
export const EntityAttachmentsButton: FC<EntityAttachmentsButtonProps> = ({
    attachmentsInfo,
    canUpload,
    canDownload,
    canDelete,
}) => {
    const { isOpen, open, close } = useModalState();
    const {
        attachments,
        options,
        upload,
        uploadProgress,
        download,
        delete: deleteAttachment,
        invalidateList,
    } = attachmentsInfo;

    // Refetch the list whenever the BE broadcasts an attachment-cache-invalidation signal
    // (driven by entity-auditing events emitted from the Documents service on UploadStatus / ScanStatus updates).
    useRemoteCacheInvalidation('attachments', invalidateList);

    const hasOptions = options !== undefined;

    return (
        <>
            <Button key="attachments" onClick={open} appearance="transparent" locked={!hasOptions} label="Anhänge">
                <IconPaperclip size={defaultIconSize} />
            </Button>
            {hasOptions && (
                <AttachmentsModal
                    mode="edit"
                    isOpen={isOpen}
                    onClose={close}
                    attachments={attachments}
                    allowedMimeTypes={options.allowed_mime_types}
                    maxFileSize={options.max_file_size}
                    canUpload={canUpload}
                    canDownload={canDownload}
                    canDelete={canDelete}
                    onUpload={upload}
                    onDownload={download}
                    onDelete={deleteAttachment}
                    uploadProgress={uploadProgress}
                />
            )}
        </>
    );
};
